import { useCallback, useEffect, useMemo, useState } from "react";
import { api, parseApiError } from "@/lib/api";
import { PageHeader } from "./AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { DestructiveConfirmDialog } from "@/components/ui/destructive-confirm-dialog";
import { toast } from "sonner";
import {
  Building2, Building, Users2, Plus, Pencil, Trash2,
  ChevronRight, ChevronDown, MapPin,
} from "lucide-react";

const NODE_KIND = {
  company: { icon: Building2, label: "Pháp nhân", color: "text-emerald-900 bg-emerald-50" },
  branch:  { icon: Building,  label: "Chi nhánh", color: "text-amber-900 bg-amber-50" },
  department: { icon: Users2, label: "Phòng ban", color: "text-stone-900 bg-stone-100" },
};

const emptyForm = {
  code: "", name: "", tax_code: "", address: "",
  parent_id: "", company_id: "", branch_id: "",
  manager_name: "", description: "",
  latitude: "", longitude: "", attendance_radius_m: "",
};

export default function Companies() {
  const [companies, setCompanies] = useState([]);
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(() => new Set());

  const [dialog, setDialog] = useState({ open: false, kind: null, mode: null, initial: null });

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [c, b, d] = await Promise.all([
        api.get("/companies"),
        api.get("/branches"),
        api.get("/departments"),
      ]);
      setCompanies(c.data || []);
      setBranches(b.data || []);
      setDepartments(d.data || []);
    } catch (e) {
      setError(parseApiError(e, "Không tải được dữ liệu").message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const tree = useMemo(() => buildTree(companies, branches, departments), [companies, branches, departments]);

  // Auto-expand top-level companies on first load
  useEffect(() => {
    if (companies.length && expanded.size === 0) {
      setExpanded(new Set(companies.filter((c) => !c.parent_id).map((c) => `company-${c.id}`)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companies.length]);

  const toggle = (key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const openCreate = (kind, parentCtx = {}) => {
    setDialog({
      open: true, kind, mode: "create",
      initial: { ...emptyForm, ...parentCtx },
    });
  };

  const openEdit = (kind, row) => {
    setDialog({
      open: true, kind, mode: "edit",
      initial: {
        ...emptyForm,
        ...row,
        parent_id: row.parent_id || "",
        company_id: row.company_id || "",
        branch_id: row.branch_id || "",
        tax_code: row.tax_code || "",
        address: row.address || "",
        manager_name: row.manager_name || "",
        description: row.description || "",
        latitude: row.latitude ?? "",
        longitude: row.longitude ?? "",
        attendance_radius_m: row.attendance_radius_m ?? "",
      },
    });
  };

  const closeDialog = () => setDialog({ open: false, kind: null, mode: null, initial: null });

  const handleSaved = () => { closeDialog(); load(); };

  const remove = async (kind, row) => {
    const path = kind === "company" ? "companies" : kind === "branch" ? "branches" : "departments";
    try {
      await api.delete(`/${path}/${row.id}`);
      toast.success("Đã xóa");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Xóa thất bại");
    }
  };

  return (
    <div data-testid="page-companies">
      <PageHeader
        kicker="Tổ chức"
        title="Pháp nhân, công ty con & phòng ban"
        breadcrumb={["Tổ chức", "Pháp nhân"]}
        actions={(
          <Button
            onClick={() => openCreate("company")}
            size="sm"
            className="bg-emerald-800 hover:bg-emerald-900 rounded-sm"
            data-testid="btn-create-company-root"
          >
            <Plus className="w-4 h-4 mr-1" /> Thêm pháp nhân
          </Button>
        )}
      />
      <div className="p-6 lg:p-8 space-y-4">
        {loading && <div className="text-stone-500 text-sm">Đang tải...</div>}
        {!loading && error && (
          <div className="bg-red-50 border border-red-200 rounded-sm p-4 text-sm text-red-800">
            {error}
            <button onClick={load} className="ml-3 underline">Thử lại</button>
          </div>
        )}
        {!loading && !error && (
          <div className="bg-white border border-stone-200 rounded-sm">
            {tree.length === 0 && (
              <div className="p-8 text-center text-stone-500 text-sm">
                Chưa có pháp nhân nào. Bấm "Thêm pháp nhân" để bắt đầu.
              </div>
            )}
            <ul className="divide-y divide-stone-100">
              {tree.map((node) => (
                <TreeNode
                  key={node.key}
                  node={node}
                  depth={0}
                  expanded={expanded}
                  onToggle={toggle}
                  onCreate={openCreate}
                  onEdit={openEdit}
                  onDelete={remove}
                />
              ))}
            </ul>
          </div>
        )}

        <Legend />
      </div>

      {dialog.open && (
        <NodeFormDialog
          kind={dialog.kind}
          mode={dialog.mode}
          initial={dialog.initial}
          companies={companies}
          branches={branches}
          departments={departments}
          onClose={closeDialog}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}

function buildTree(companies, branches, departments) {
  const companyMap = new Map(companies.map((c) => [c.id, { ...c, _children: [] }]));
  const roots = [];
  companyMap.forEach((c) => {
    if (c.parent_id && companyMap.has(c.parent_id)) {
      companyMap.get(c.parent_id)._children.push(c);
    } else {
      roots.push(c);
    }
  });

  const branchesByCompany = new Map();
  branches.forEach((b) => {
    if (!branchesByCompany.has(b.company_id)) branchesByCompany.set(b.company_id, []);
    branchesByCompany.get(b.company_id).push(b);
  });

  const deptMap = new Map(departments.map((d) => [d.id, { ...d, _children: [] }]));
  const deptRootsByContext = new Map(); // key = `${companyId}|${branchId||""}`
  deptMap.forEach((d) => {
    if (d.parent_id && deptMap.has(d.parent_id)) {
      deptMap.get(d.parent_id)._children.push(d);
    } else {
      const k = `${d.company_id}|${d.branch_id || ""}`;
      if (!deptRootsByContext.has(k)) deptRootsByContext.set(k, []);
      deptRootsByContext.get(k).push(d);
    }
  });

  const toCompanyNode = (c) => {
    const children = [];
    // Subsidiary companies
    (c._children || []).forEach((sub) => children.push(toCompanyNode(sub)));
    // Branches under this company
    (branchesByCompany.get(c.id) || []).forEach((b) => children.push(toBranchNode(b)));
    // Departments at company level (no branch)
    (deptRootsByContext.get(`${c.id}|`) || []).forEach((d) => children.push(toDeptNode(d)));
    return {
      key: `company-${c.id}`,
      kind: "company",
      row: c,
      children,
    };
  };

  const toBranchNode = (b) => {
    const deptRoots = deptRootsByContext.get(`${b.company_id}|${b.id}`) || [];
    return {
      key: `branch-${b.id}`,
      kind: "branch",
      row: b,
      children: deptRoots.map(toDeptNode),
    };
  };

  const toDeptNode = (d) => ({
    key: `department-${d.id}`,
    kind: "department",
    row: d,
    children: (d._children || []).map(toDeptNode),
  });

  return roots.map(toCompanyNode);
}

function TreeNode({ node, depth, expanded, onToggle, onCreate, onEdit, onDelete }) {
  const { kind, row, children, key } = node;
  const conf = NODE_KIND[kind];
  const Icon = conf.icon;
  const hasChildren = children.length > 0;
  const isOpen = expanded.has(key);

  const subtitle = (() => {
    if (kind === "company") {
      const parts = [];
      if (row.tax_code) parts.push(`MST: ${row.tax_code}`);
      if (row.parent_id) parts.push("Công ty con");
      return parts.join(" · ");
    }
    if (kind === "branch") {
      return row.company_name ? `Thuộc ${row.company_name}` : "";
    }
    if (kind === "department") {
      const parts = [];
      if (row.manager_name) parts.push(`Trưởng phòng: ${row.manager_name}`);
      if (row.branch_name) parts.push(`@ ${row.branch_name}`);
      return parts.join(" · ");
    }
    return "";
  })();

  return (
    <li>
      <div
        className="flex items-center gap-2 px-3 py-2 hover:bg-stone-50 group"
        style={{ paddingLeft: 12 + depth * 24 }}
        data-testid={`tree-node-${kind}-${row.code || row.id}`}
      >
        <button
          onClick={() => hasChildren && onToggle(key)}
          className={`shrink-0 w-5 h-5 flex items-center justify-center rounded-sm ${hasChildren ? "hover:bg-stone-200" : "invisible"}`}
          aria-label={isOpen ? "Thu gọn" : "Mở rộng"}
        >
          {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>

        <div className={`shrink-0 w-8 h-8 rounded-sm flex items-center justify-center ${conf.color}`}>
          <Icon className="w-4 h-4" strokeWidth={1.75} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[10px] uppercase tracking-wider text-stone-500 font-bold">{row.code}</span>
            <span className="text-[10px] uppercase tracking-wider text-stone-400">{conf.label}</span>
          </div>
          <div className="font-semibold text-sm truncate">{row.name}</div>
          {subtitle && (
            <div className="text-xs text-stone-500 mt-0.5 flex items-center gap-1">
              {kind === "branch" && row.address && <MapPin className="w-3 h-3 shrink-0" />}
              <span className="truncate">{subtitle}{kind === "branch" && row.address ? ` · ${row.address}` : ""}</span>
            </div>
          )}
        </div>

        <div className="shrink-0 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
          {kind === "company" && (
            <>
              <ActionBtn
                title="Thêm công ty con"
                onClick={() => onCreate("company", { parent_id: row.id })}
                testid={`btn-add-sub-${row.code}`}
              >
                <Building2 className="w-3.5 h-3.5" />
                <Plus className="w-3 h-3 -ml-1" />
              </ActionBtn>
              <ActionBtn
                title="Thêm chi nhánh"
                onClick={() => onCreate("branch", { company_id: row.id })}
                testid={`btn-add-branch-${row.code}`}
              >
                <Building className="w-3.5 h-3.5" />
                <Plus className="w-3 h-3 -ml-1" />
              </ActionBtn>
              <ActionBtn
                title="Thêm phòng ban"
                onClick={() => onCreate("department", { company_id: row.id })}
                testid={`btn-add-dept-${row.code}`}
              >
                <Users2 className="w-3.5 h-3.5" />
                <Plus className="w-3 h-3 -ml-1" />
              </ActionBtn>
            </>
          )}
          {kind === "branch" && (
            <ActionBtn
              title="Thêm phòng ban"
              onClick={() => onCreate("department", { company_id: row.company_id, branch_id: row.id })}
              testid={`btn-add-dept-${row.code}`}
            >
              <Users2 className="w-3.5 h-3.5" />
              <Plus className="w-3 h-3 -ml-1" />
            </ActionBtn>
          )}
          {kind === "department" && (
            <ActionBtn
              title="Thêm phòng con"
              onClick={() => onCreate("department", { company_id: row.company_id, branch_id: row.branch_id, parent_id: row.id })}
              testid={`btn-add-subdept-${row.code}`}
            >
              <Users2 className="w-3.5 h-3.5" />
              <Plus className="w-3 h-3 -ml-1" />
            </ActionBtn>
          )}
          <ActionBtn title="Sửa" onClick={() => onEdit(kind, row)} testid={`btn-edit-${kind}-${row.code}`}>
            <Pencil className="w-3.5 h-3.5" />
          </ActionBtn>
          <DestructiveConfirmDialog
            title={`Xóa ${conf.label.toLowerCase()}?`}
            description={`${conf.label} "${row.name}" (${row.code}) sẽ bị xóa vĩnh viễn. Hãy nhập mã ${row.code} để xác nhận.`}
            confirmLabel="Xóa"
            token={String(row.code || row.id)}
            onConfirm={() => onDelete(kind, row)}
            trigger={(
              <ActionBtn title="Xóa" testid={`btn-del-${kind}-${row.code}`} danger>
                <Trash2 className="w-3.5 h-3.5" />
              </ActionBtn>
            )}
          />
        </div>
      </div>

      {hasChildren && isOpen && (
        <ul className="divide-y divide-stone-100">
          {children.map((child) => (
            <TreeNode
              key={child.key}
              node={child}
              depth={depth + 1}
              expanded={expanded}
              onToggle={onToggle}
              onCreate={onCreate}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

function ActionBtn({ title, onClick, children, testid, danger = false }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center px-1.5 h-7 rounded-sm border border-stone-200 bg-white hover:bg-stone-100 ${danger ? "text-red-700 hover:text-red-800" : "text-stone-700"}`}
    >
      {children}
    </button>
  );
}

function Legend() {
  return (
    <div className="text-xs text-stone-500 flex flex-wrap items-center gap-x-4 gap-y-1 px-1">
      <span className="inline-flex items-center gap-1"><Building2 className="w-3.5 h-3.5 text-emerald-900" /> Pháp nhân / Công ty con</span>
      <span className="inline-flex items-center gap-1"><Building className="w-3.5 h-3.5 text-amber-800" /> Chi nhánh</span>
      <span className="inline-flex items-center gap-1"><Users2 className="w-3.5 h-3.5 text-stone-700" /> Phòng ban</span>
      <span className="ml-auto text-stone-400">Di chuột vào hàng để hiện hành động</span>
    </div>
  );
}

function NodeFormDialog({ kind, mode, initial, companies, branches, departments, onClose, onSaved }) {
  const [form, setForm] = useState(initial || emptyForm);
  const [saving, setSaving] = useState(false);
  const conf = NODE_KIND[kind];

  const updateField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const branchOptions = useMemo(() => {
    if (!form.company_id) return [];
    return branches.filter((b) => b.company_id === form.company_id);
  }, [branches, form.company_id]);

  const departmentParentOptions = useMemo(() => {
    if (kind !== "department") return [];
    return departments.filter((d) =>
      d.company_id === form.company_id
      && (!form.branch_id || d.branch_id === form.branch_id || !d.branch_id)
      && d.id !== form.id,
    );
  }, [departments, form.company_id, form.branch_id, form.id, kind]);

  const companyParentOptions = useMemo(() => {
    if (kind !== "company") return [];
    return companies.filter((c) => c.id !== form.id);
  }, [companies, form.id, kind]);

  const save = async () => {
    setSaving(true);
    try {
      const path = kind === "company" ? "companies" : kind === "branch" ? "branches" : "departments";

      const payload = (() => {
        if (kind === "company") {
          return {
            code: form.code,
            name: form.name,
            tax_code: form.tax_code || null,
            address: form.address || null,
            parent_id: form.parent_id || null,
          };
        }
        if (kind === "branch") {
          return {
            code: form.code,
            name: form.name,
            company_id: form.company_id,
            address: form.address || null,
            latitude: form.latitude === "" ? null : Number(form.latitude),
            longitude: form.longitude === "" ? null : Number(form.longitude),
            attendance_radius_m: form.attendance_radius_m === "" ? null : Number(form.attendance_radius_m),
          };
        }
        return {
          code: form.code,
          name: form.name,
          company_id: form.company_id,
          branch_id: form.branch_id || null,
          parent_id: form.parent_id || null,
          manager_name: form.manager_name || null,
          description: form.description || null,
        };
      })();

      if (mode === "edit") {
        await api.put(`/${path}/${initial.id}`, payload);
      } else {
        await api.post(`/${path}`, payload);
      }
      toast.success(mode === "edit" ? "Đã cập nhật" : "Đã thêm");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lưu thất bại");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="rounded-sm max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">
            {mode === "edit" ? "Sửa" : "Thêm"} {conf.label.toLowerCase()}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Mã *">
            <Input
              value={form.code}
              onChange={(e) => updateField("code", e.target.value)}
              className="h-9 rounded-sm"
              data-testid={`f-${kind}-code`}
            />
          </Field>
          <Field label="Tên *">
            <Input
              value={form.name}
              onChange={(e) => updateField("name", e.target.value)}
              className="h-9 rounded-sm"
              data-testid={`f-${kind}-name`}
            />
          </Field>

          {kind === "company" && (
            <>
              <Field label="Mã số thuế">
                <Input
                  value={form.tax_code}
                  onChange={(e) => updateField("tax_code", e.target.value)}
                  className="h-9 rounded-sm"
                  data-testid={`f-${kind}-tax_code`}
                />
              </Field>
              <Field label="Pháp nhân mẹ (nếu là công ty con)">
                <SelectField
                  value={form.parent_id}
                  onChange={(v) => updateField("parent_id", v)}
                  placeholder="— Không có (pháp nhân chính) —"
                  options={companyParentOptions.map((c) => ({ value: c.id, label: `${c.code} · ${c.name}` }))}
                  testid={`f-${kind}-parent_id`}
                />
              </Field>
              <Field label="Địa chỉ" full>
                <Textarea
                  rows={2}
                  value={form.address}
                  onChange={(e) => updateField("address", e.target.value)}
                  className="rounded-sm"
                  data-testid={`f-${kind}-address`}
                />
              </Field>
            </>
          )}

          {kind === "branch" && (
            <>
              <Field label="Pháp nhân *" full>
                <SelectField
                  value={form.company_id}
                  onChange={(v) => updateField("company_id", v)}
                  placeholder="— Chọn pháp nhân —"
                  options={companies.map((c) => ({ value: c.id, label: `${c.code} · ${c.name}` }))}
                  testid={`f-${kind}-company_id`}
                />
              </Field>
              <Field label="Địa chỉ" full>
                <Textarea
                  rows={2}
                  value={form.address}
                  onChange={(e) => updateField("address", e.target.value)}
                  className="rounded-sm"
                  data-testid={`f-${kind}-address`}
                />
              </Field>
              <Field label="Latitude">
                <Input
                  type="number"
                  step="any"
                  value={form.latitude}
                  onChange={(e) => updateField("latitude", e.target.value)}
                  className="h-9 rounded-sm"
                  data-testid={`f-${kind}-lat`}
                />
              </Field>
              <Field label="Longitude">
                <Input
                  type="number"
                  step="any"
                  value={form.longitude}
                  onChange={(e) => updateField("longitude", e.target.value)}
                  className="h-9 rounded-sm"
                  data-testid={`f-${kind}-lng`}
                />
              </Field>
              <Field label="Bán kính chấm công (m)">
                <Input
                  type="number"
                  value={form.attendance_radius_m}
                  onChange={(e) => updateField("attendance_radius_m", e.target.value)}
                  className="h-9 rounded-sm"
                  data-testid={`f-${kind}-radius`}
                />
              </Field>
            </>
          )}

          {kind === "department" && (
            <>
              <Field label="Pháp nhân *">
                <SelectField
                  value={form.company_id}
                  onChange={(v) => updateField("company_id", v === form.company_id ? v : v)}
                  placeholder="— Chọn —"
                  options={companies.map((c) => ({ value: c.id, label: `${c.code} · ${c.name}` }))}
                  testid={`f-${kind}-company_id`}
                />
              </Field>
              <Field label="Chi nhánh (nếu có)">
                <SelectField
                  value={form.branch_id}
                  onChange={(v) => updateField("branch_id", v)}
                  placeholder="— Cấp pháp nhân —"
                  options={branchOptions.map((b) => ({ value: b.id, label: `${b.code} · ${b.name}` }))}
                  testid={`f-${kind}-branch_id`}
                />
              </Field>
              <Field label="Phòng ban cha">
                <SelectField
                  value={form.parent_id}
                  onChange={(v) => updateField("parent_id", v)}
                  placeholder="— Không có —"
                  options={departmentParentOptions.map((d) => ({ value: d.id, label: `${d.code} · ${d.name}` }))}
                  testid={`f-${kind}-parent_id`}
                />
              </Field>
              <Field label="Trưởng phòng">
                <Input
                  value={form.manager_name}
                  onChange={(e) => updateField("manager_name", e.target.value)}
                  className="h-9 rounded-sm"
                  data-testid={`f-${kind}-manager`}
                />
              </Field>
              <Field label="Mô tả" full>
                <Textarea
                  rows={2}
                  value={form.description}
                  onChange={(e) => updateField("description", e.target.value)}
                  className="rounded-sm"
                  data-testid={`f-${kind}-desc`}
                />
              </Field>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm">Hủy</Button>
          <Button
            onClick={save}
            disabled={saving}
            className="bg-emerald-800 hover:bg-emerald-900 rounded-sm"
            data-testid={`btn-save-${kind}`}
          >
            {saving ? "Đang lưu..." : "Lưu"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, full = false }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <Label className="text-xs">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

const NONE_VALUE = "__none__";

function SelectField({ value, onChange, placeholder, options, testid }) {
  return (
    <Select
      value={value || NONE_VALUE}
      onValueChange={(v) => onChange(v === NONE_VALUE ? "" : v)}
    >
      <SelectTrigger className="h-9 rounded-sm" data-testid={testid}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={NONE_VALUE}>{placeholder}</SelectItem>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
